import { ARC_COMPLIANCE_KINDS, ARC_COMPLIANCE_STATUSES, ARC_REFERENCE_KINDS, } from '../database/project.schema.js';
import { ENGINE_WORKSCOPE_LEVELS } from '../ProcessingData.js';
import z from 'zod';
/**
 * A project's Authorized Release Certificate rows (DR-3389) — every part-release
 * line the project's ARC documents assert, unioned across the six release-certificate
 * categories.
 *
 * Emitted as ONE webhook per project carrying a flat array, mirroring the
 * thrust-conversion / engine-events families: there is no single winning document
 * to pick (accessory and engine certificates must all ship). Each row keeps the
 * `documentId` of its source document and the `pageId` it was extracted from.
 *
 * `esn` is the project's working engine — informational only; rows are NOT scoped
 * to it. Nullable for the same reason the engine family's is.
 *
 * Oil has no columns for `formType` / `statusRaw` / `approvalNumber`, so those
 * extraction fields are not on the wire. `authority` ships from DR-4301 so a
 * non-FAA / non-EASA form can be labelled by regulator. `releaseScope`,
 * `releaseScopeEvidence` and `releaseScopeRuleVersion` (DR-4436) are additive: Oil has no
 * columns for them yet, and Oil/v2 map them in DR-4437. The since-event clocks `tso` / `cso` /
 * `tsr` / `csr` / `tsi` / `csi` (DR-4542) are additive too; Oil stores them in DR-4541. The
 * certificate workscope level `workscopeLevel` / `workscopeLevelRaw` / `workscopeLevelRuleVersion`
 * (DR-4602) is additive too, and so are the Block-12 printed references `references` /
 * `workscopeReference` / `workscopeReferenceEvidence` / `referenceRuleVersion` (DR-4726; v1 reads
 * the remarks only, not Block 5 or attachment pages), and so are the AD / SB compliance references
 * `complianceReferences` / `complianceReferenceRuleVersion` (DR-5549). `pageIds` and `reconstructedDocumentId` (DR-4724) are additive
 * too: a certificate whose engine clocks sit on an attachment page lists that page. No `snapshotVersion`.
 */
export const arcEventsReadyWebhookSchema = z.looseObject({
    type: z.literal('arc-events-ready'),
    projectId: z.string(),
    // `z.iso.datetime()`, not a bare string: the consumer contract is an ORDERABLE instant,
    // so the schema enforces what the field documents. Follows `pma-der-events-ready` /
    // `document-header-ready` rather than the looser `project-progress` / `project-data-ready`
    // spelling.
    //
    // `.optional()` is a ROLLOUT concession, not a statement that the stamp is dispensable —
    // Brain always emits it (see the family's builder, pinned by its test). This schema is a
    // member of `webhookPayloadSchema`, so a REQUIRED field would make a consumer that
    // upgrades `@avisoma/shared` reject every older, unstamped envelope still in flight — and reject
    // ALL of them for as long as the consumer runs ahead of Brain's own deploy. Tighten to
    // required in a later release, once no unstamped envelope can still be in flight (DR-3278).
    generatedAt: z.iso.datetime().optional(),
    generation: z.number().int().positive().max(2_147_483_647).optional(),
    esn: z.string().nullable(),
    arcEvents: z.array(z.looseObject({
        partNumber: z.string(),
        serialNumber: z.string().optional(),
        quantity: z.number().optional(),
        description: z.string().optional(),
        // Oil's status vocabulary — MODIFIED / PROTOTYPE / USED stay off the wire.
        status: z.enum(['OH', 'RP', 'INSP', 'UNS', 'NEW', 'AR']).optional(),
        tsn: z.number().optional(),
        csn: z.number().optional(),
        // Since-event clocks read by Brain from the Block-12 remarks (DR-4542): hours / cycles
        // since overhaul, repair and inspection. Only on the sole row of its certificate; omitted
        // when not printed, unknown or ambiguous.
        tso: z.number().optional(),
        cso: z.number().optional(),
        tsr: z.number().optional(),
        csr: z.number().optional(),
        tsi: z.number().optional(),
        csi: z.number().optional(),
        inspectionDescription: z.string().optional(),
        date: z.iso.date().optional(),
        isSigned: z.boolean().optional(),
        isFaaCertified: z.boolean().optional(),
        isEasaCertified: z.boolean().optional(),
        // Open string, not an enum: extraction fails open for unlisted regulators.
        // Omitted when unreadable or absent (DR-4301). Dual FAA+EASA stays on the flags.
        authority: z.string().min(1).optional(),
        formTrackingNumber: z.string().optional(),
        exESN: z.string().optional(),
        workOrder: z.string().optional(),
        organizationName: z.string().optional(),
        momentWeight: z.number().optional(),
        certificateKind: z.enum(['correction']).optional(),
        correctsFormTrackingNumber: z.string().optional(),
        correctsDate: z.iso.date().optional(),
        coversReleaseToService: z.boolean().optional(),
        // Certificate-level, derived by Brain from the Block-12 remarks (DR-4436). Brain stamps
        // all three on every row; `unknown` when the certificate has no remarks.
        releaseScope: z
            .enum(['shop-visit', 'minor-repair', 'on-wing-maintenance', 'inspection-only', 'unknown'])
            .optional(),
        // Matched remark signals, each with a verbatim quote of at most 160 chars. Open string
        // `signal` so a later rule version can add signals without breaking consumers.
        releaseScopeEvidence: z.array(z.object({ signal: z.string(), quote: z.string() })).optional(),
        releaseScopeRuleVersion: z.number().int().optional(),
        // Certificate-level depth of the shop visit, derived by Brain from printed Block-12 wording
        // (DR-4602). Omitted when no rule matched; `workscopeLevelRaw` is the verbatim phrase (at
        // most 64 chars), and Brain stamps `workscopeLevelRuleVersion` on every row.
        workscopeLevel: z.enum(ENGINE_WORKSCOPE_LEVELS).optional(),
        workscopeLevelRaw: z.string().optional(),
        workscopeLevelRuleVersion: z.number().int().optional(),
        // Certificate-level labelled order / workscope references printed in the Block-12 remarks
        // (DR-4726), deduped, in text order; omitted when none. v1 reads the remarks only, not
        // Block 5 or attachment pages. `workscopeReference` is the single distinct workscope /
        // repair-order / customer-order value, `null` when none or several; Brain stamps it and
        // `referenceRuleVersion` on every row. `workOrder` is unchanged.
        references: z
            .array(z.object({ kind: z.enum(ARC_REFERENCE_KINDS), label: z.string(), value: z.string() }))
            .optional(),
        workscopeReference: z.string().nullable().optional(),
        workscopeReferenceEvidence: z.string().optional(),
        referenceRuleVersion: z.number().int().optional(),
        // Certificate-level AD / SB numbers printed in the Block-12 remarks (DR-5549), one entry per
        // distinct assertion, in text order: the number as printed (`_` folded to `-`, revision in its
        // own field), the printed `issuer`, the compliance `status` and a verbatim `quote` (at most
        // 240 chars). The list is unbounded by design: one entry per distinct printed assertion.
        // Brain stamps `[]` when the remarks hold no numbered statement, and
        // `complianceReferenceRuleVersion` on every row. Optional for rows stored before DR-5549.
        complianceReferences: z
            .array(z.object({
            kind: z.enum(ARC_COMPLIANCE_KINDS),
            number: z.string(),
            issuer: z.string().optional(),
            revision: z.string().optional(),
            status: z.enum(ARC_COMPLIANCE_STATUSES),
            quote: z.string().max(240),
        }))
            .optional(),
        complianceReferenceRuleVersion: z.number().int().optional(),
        documentId: z.string(),
        pageId: z.string(),
        // Every page the row was read from, `pageId` first, then the certificate's attachment
        // pages (DR-4724), and the reconstructed document it was published from. Additive.
        pageIds: z.array(z.string()).optional(),
        reconstructedDocumentId: z.string().optional(),
    })),
});
//# sourceMappingURL=arc-events-ready.webhook.js.map